/**
 * Componente: Campanita de notificaciones de vencimiento
 * Visible en el navbar para admin y operador, en cualquier sección.
 * Con esAdmin=true, suma una sección de solicitudes de autorización
 * pendientes (mismo endpoint que usa Auditoria.jsx) — para operador esa
 * sección no existe, el panel queda igual que siempre.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { IconNotificaciones } from './icons';

const API_URL = process.env.REACT_APP_API_URL;

const ETIQUETAS_ESTADO = {
  proxima_a_vencer: 'Próxima a vencer',
  vencida_en_gracia: 'En período de gracia',
  vencida_fuera_de_gracia: 'Vencida',
};

const TIPOS_SOLICITUD = {
  reintento_impresion: 'Reintento de impresión',
  reimpresion: 'Reimpresión',
  reactivacion: 'Reactivación de licencia',
};

const formatearDias = (dias, estado) => {
  if (estado === 'proxima_a_vencer') return `vence en ${Math.abs(dias)} días`;
  if (dias === 0) return 'vence hoy';
  return `vencida hace ${dias} días`;
};

const NotificacionesBell = ({ esAdmin = false, onIrAAuditoria }) => {
  const [resumen, setResumen] = useState({ conteos: { total: 0 }, alertas: [] });
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const contenedorRef = useRef(null);

  const cargarResumen = useCallback(async () => {
    try {
      setCargando(true);
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [resumenRes, solicitudesRes] = await Promise.all([
        axios.get(`${API_URL}/notificaciones/resumen`, { headers }),
        // Solo admin tiene acceso a este endpoint (ver Auditoria.jsx) — un
        // operador pidiéndolo daría 403, así que ni se llama.
        esAdmin
          ? axios.get(`${API_URL}/solicitudes-impresion`, { headers, params: { estado: 'pendiente' } })
          : Promise.resolve(null),
      ]);
      setResumen(resumenRes.data);
      setSolicitudesPendientes(esAdmin ? (solicitudesRes.data.solicitudes || []) : []);
    } catch (err) {
      console.error('Error al cargar notificaciones:', err);
    } finally {
      setCargando(false);
    }
  }, [esAdmin]);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  useEffect(() => {
    const cerrarSiClickAfuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener('mousedown', cerrarSiClickAfuera);
    return () => document.removeEventListener('mousedown', cerrarSiClickAfuera);
  }, []);

  const alClickCampana = () => {
    const seVaAAbrir = !abierto;
    setAbierto(seVaAAbrir);
    if (seVaAAbrir) cargarResumen();
  };

  const irAAuditoria = () => {
    setAbierto(false);
    onIrAAuditoria();
  };

  // Un contribuyente con solicitud pendiente ya se muestra en la sección
  // de arriba — sacarlo de las alertas de vencimiento evita que aparezca
  // duplicado en las dos secciones del mismo panel. El total y el badge
  // reflejan esta lista ya filtrada, no el total crudo que manda el backend.
  const idsConSolicitudPendiente = new Set(solicitudesPendientes.map((s) => s.contribuyente_id));
  const alertasFiltradas = resumen.alertas.filter((a) => !idsConSolicitudPendiente.has(a.id));

  const totalVencimientos = alertasFiltradas.length;
  const totalSolicitudes = esAdmin ? solicitudesPendientes.length : 0;
  const totalBadge = totalVencimientos + totalSolicitudes;

  return (
    <div className="notificaciones-bell" ref={contenedorRef}>
      <button className="bell-button" onClick={alClickCampana} aria-label="Notificaciones de vencimiento">
        <IconNotificaciones size={20} />
        {totalBadge > 0 && <span className="bell-badge">{totalBadge > 99 ? '99+' : totalBadge}</span>}
      </button>

      {abierto && (
        <div className="bell-panel">
          <div className="bell-panel-header">
            <span>Alertas de vencimiento</span>
            <span className="bell-panel-total">{totalVencimientos}</span>
          </div>

          <div className="bell-panel-list">
            {cargando ? (
              <div className="bell-empty">Cargando...</div>
            ) : (
              <>
                {totalSolicitudes > 0 && (
                  <div className="bell-solicitudes-section">
                    <div className="bell-solicitudes-header">
                      <span>Solicitudes pendientes</span>
                      <span className="bell-solicitudes-total">{totalSolicitudes}</span>
                    </div>
                    {solicitudesPendientes.map((s) => (
                      <div key={s.id} className="bell-item">
                        <div className="bell-item-nombre">{s.apellido}, {s.contribuyente_nombre}</div>
                        <div className="bell-item-meta">
                          <span className="bell-item-tipo">{TIPOS_SOLICITUD[s.tipo] || s.tipo}</span>
                          <button type="button" className="bell-item-ver-auditoria" onClick={irAAuditoria}>
                            Ver en Auditoría →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {alertasFiltradas.length === 0 ? (
                  <div className="bell-empty">No hay licencias en alerta</div>
                ) : (
                  alertasFiltradas.map((alerta) => (
                    <div key={alerta.id} className="bell-item">
                      <div className="bell-item-nombre">{alerta.nombre_completo}</div>
                      <div className="bell-item-meta">
                        <span className={`bell-item-estado bell-item-estado-${alerta.estado_licencia}`}>
                          {ETIQUETAS_ESTADO[alerta.estado_licencia]}
                        </span>
                        <span className="bell-item-dias">{formatearDias(alerta.dias, alerta.estado_licencia)}</span>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificacionesBell;
